import { ConvexError } from 'convex/values'

// Ephemeral xAI client secret for a browser WebSocket (`xai-client-secret.<token>`
// subprotocol). Accepted by both /v1/realtime (calls) and /v1/stt (dictation), so it
// isn't scoped and the session outlives it: keep lifetimes just long enough to connect.
export async function mintRealtimeToken(
  seconds = 300,
  failMessage = 'Could not start the call. Please try again.',
): Promise<string> {
  const apiKey = process.env.XAI_API_KEY
  if (!apiKey) throw new ConvexError('Voice is not configured.')
  let res: Response
  try {
    res = await fetch('https://api.x.ai/v1/realtime/client_secrets', {
      method: 'POST',
      headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ expires_after: { seconds } }),
    })
  } catch {
    throw new ConvexError(failMessage)
  }
  if (!res.ok) {
    console.error('[xai] client_secret mint failed', res.status, await res.text().catch(() => ''))
    throw new ConvexError(failMessage)
  }
  let data: { value?: string; client_secret?: { value?: string } | string }
  try {
    data = await res.json()
  } catch {
    throw new ConvexError(failMessage)
  }
  const token =
    data.value ??
    (typeof data.client_secret === 'string' ? data.client_secret : data.client_secret?.value)
  if (!token) throw new ConvexError(failMessage)
  return token
}

export const STT_SAMPLE_RATE = 16_000
// Inverse text normalization ("twenty books" → "20 books") needs a language.
const ITN_LANGS = new Set(['ar', 'de', 'en', 'es', 'fr', 'ja', 'pt', 'ru', 'sv', 'vi', 'zh'])

export function sttUrl(language: string | undefined, keyterms: string[]): string {
  const lang = language?.split('-')[0]
  const qs = new URLSearchParams({
    sample_rate: String(STT_SAMPLE_RATE),
    encoding: 'pcm',
    interim_results: 'true',
    ...(lang ? { language: lang } : {}),
    ...(lang && ITN_LANGS.has(lang) ? { format: 'true' } : {}),
  })
  for (const term of keyterms) qs.append('keyterm', term)
  return `wss://api.x.ai/v1/stt?${qs}`
}
